import { aiOpenapiClient, openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  AllergenTableEntry,
  ParseAllergenSheetBody,
  SetAllergenSheetBody,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

function failMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object' && 'message' in body) {
    return String((body as { message: unknown }).message);
  }
  return `Request failed (${status})`;
}

function useRestaurantId() {
  return useRestaurantStore((s) => s.selectedRestaurantId);
}

export function useAllergenSheet() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['allergen-sheet', restaurantId],
    enabled: !!restaurantId,
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/allergen-sheet', {
          params: { path: { restaurantId: restaurantId ?? '' } },
        }),
      );
    },
  });
}

export function useSetAllergenSheet() {
  const restaurantId = useRestaurantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetAllergenSheetBody) =>
      unwrapResult(
        await openapiClient.PUT('/api/restaurants/{restaurantId}/allergen-sheet', {
          params: { path: { restaurantId: restaurantId ?? '' } },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allergen-sheet', restaurantId] }),
  });
}

export function useAllergenTable() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['allergen-table', restaurantId],
    enabled: !!restaurantId,
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/allergen-table', {
          params: { path: { restaurantId: restaurantId ?? '' } },
        }),
      );
    },
  });
}

export function useSetAllergenTable() {
  const restaurantId = useRestaurantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entries: AllergenTableEntry[]) =>
      unwrapResult(
        await openapiClient.PUT('/api/restaurants/{restaurantId}/allergen-table', {
          params: { path: { restaurantId: restaurantId ?? '' } },
          body: { entries },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['allergen-table', restaurantId] });
      qc.invalidateQueries({ queryKey: ['menu'] });
    },
  });
}

export function useParseAllergenSheet() {
  const restaurantId = useRestaurantId();
  return useMutation({
    mutationFn: async (input: ParseAllergenSheetBody) => {
      const { data, error, response } = await aiOpenapiClient.POST(
        '/restaurants/{restaurantId}/ai/allergen-sheet/parse',
        { params: { path: { restaurantId: restaurantId ?? '' } }, body: input },
      );
      if (response.status !== 200 || !data) throw new Error(failMessage(error, response.status));
      return data.entries;
    },
  });
}
